import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, Provider } from '@angular/core';
import { provideRouter, withPreloading, PreloadAllModules, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { provideWlcmDatepicker } from '@wlcm/angular/datepicker';
import { provideWlcmFormErrors } from '@wlcm/angular/forms';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { apiUrlInterceptor, errorInterceptor } from '@shared/interceptors';
import { NavigationService } from '@shared/navigation/navigation.service';
import { provideDialogs } from '@shared/modules/dialog/dialog.providers';
import { FORM_ERRORS } from '@shared/constants/forms/form-errors.constants';
import { DateRangePickerBpComponent } from '@shared/components/date-range-picker-bp/date-range-picker-bp.component';
import { APP_VERSION } from '@shared/constants/platform/app-version.token';
import { ENVIRONMENT } from '@shared/constants/platform/environment.token';
import { environment } from '@environment';
import { APP_ROUTES } from './app.routes';
import { version as appVersion } from '../../package.json';

export const WLCM_DEFAULT_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'MMM dd yyyy',
  },
  display: {
    dateInput: 'MMM dd yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'MMM dd yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

const material: Provider[] = [
  {
    provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
    useValue: { verticalPosition: 'bottom', horizontalPosition: 'end' },
  },
  {
    provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
    useValue: { position: 'above' },
  },
  {
    provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
    useFactory: (sso: ScrollStrategyOptions) => () => sso.reposition(),
    deps: [ScrollStrategyOptions],
  },
  {
    provide: MAT_RADIO_DEFAULT_OPTIONS,
    useValue: { color: 'primary' },
  },
  { provide: MAT_DATE_FORMATS, useValue: WLCM_DEFAULT_DATE_FORMATS },
];

export const APP_CONFIG: ApplicationConfig = {
  providers: [
    { provide: ENVIRONMENT, useValue: environment },
    { provide: APP_VERSION, useValue: appVersion },
    // provideWlcmDatepicker registers the date adapter and MAT_DATE_FORMATS; the material providers below must come after it to override them.
    provideWlcmDatepicker({ datepicker: { selectOnly: true }, bottomPanelComponent: DateRangePickerBpComponent }),
    provideWlcmFormErrors(FORM_ERRORS),
    importProvidersFrom(InlineSVGModule.forRoot()),
    ...material,
    provideDialogs(),
    // sub-tab routes sit under component routes (fb-route-tabs), which the default strategy
    // does not pass the details page `:id` through
    provideRouter(
      APP_ROUTES,
      withPreloading(PreloadAllModules),
      withRouterConfig({ paramsInheritanceStrategy: 'always' })
    ),
    provideHttpClient(withXhr(), withInterceptors([apiUrlInterceptor, errorInterceptor])),
    provideAnimationsAsync(),
    // Eager start, so the back-navigation history includes the first navigation.
    provideAppInitializer(() => void inject(NavigationService)),
  ],
};
